import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CosmicBackground from '../components/CosmicBackground';

const HowItWorks = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const { scrollYProgress } = useScroll();
  const navigate = useNavigate();
  
  // Only apply parallax on desktop to prevent mobile overlapping
  const y1 = useTransform(scrollYProgress, [0, 1], [0, isMobile ? 0 : -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, isMobile ? 0 : -200]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    
    handleResize(); // Set initial value
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const steps = [
    {
      number: "01",
      title: "Choose Your Plan & Provide Birth Details",
      description: "Get started by choosing a plan and providing correct birth details. Find assistance and information on your horoscope predictions over a phone call.",
      image: "/HIW-1.jpg",
      color: "from-purple-500 to-indigo-600"
    },
    {
      number: "02", 
      title: "Take Control of Your Life",
      description: "Take control of your astrological concerns with Astrohelp, a platform dedicated to understanding the cosmic influences on your life through horoscope analysis. Upon selecting a plan and providing your birth details, our experts carefully study and interpret your birth chart. We ensure a thorough and precise assessment, taking a day or two to deliver an accurate and comprehensive report. Trust us to provide detailed insights that help you align with the cosmic flow and make informed decisions.",
      image: "/HIW-2.jpg",
      color: "from-pink-500 to-purple-600"
    }
  ];

  return (
    <CosmicBackground variant="default">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        className="min-h-screen relative z-10"
      >
        <div className="pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16 md:pb-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-4">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-center mb-12 sm:mb-16 md:mb-20"
            >
              <motion.h1
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-yellow-300 via-yellow-200 to-yellow-300 bg-clip-text text-transparent leading-tight py-2 px-4 sm:px-0"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                }}
                style={{
                  backgroundSize: "200% 200%",
                }}
              >
                ✨ How We Operate ✨
              </motion.h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-200 max-w-4xl mx-auto leading-relaxed px-4 sm:px-0">
                What's on your mind? We are here to solve your riddles. Considering every area of your life, we are sure to offer the best of astrological help.
              </p>
            </motion.div>

            <div className="space-y-16 sm:space-y-20 md:space-y-32">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 100 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.3, duration: 1 }}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 sm:gap-12 md:gap-16 px-4 sm:px-0`}
                >
                  {/* Content */}
                  <div className="flex-1 space-y-6 sm:space-y-8 text-center lg:text-left">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className={`inline-block px-8 py-4 sm:px-10 sm:py-6 bg-gradient-to-r ${step.color} text-white rounded-full text-3xl sm:text-4xl md:text-5xl font-bold shadow-2xl shadow-purple-500/30`}
                    >
                      {step.number}
                    </motion.div>
                    
                    <motion.h2
                      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2, duration: 0.8 }}
                      className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2 sm:px-0"
                    >
                      {step.title}
                    </motion.h2>
                    
                    <motion.p
                      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.8 }}
                      className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed px-2 sm:px-0"
                    >
                      {step.description}
                    </motion.p>

                    {/* Additional Features */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.8 }}
                      className="space-y-3 sm:space-y-4"
                    >
                      {index === 0 ? (
                        <>
                          <div className="flex items-center space-x-3 sm:space-x-4 justify-center lg:justify-start">
                            <div className="w-3 h-3 bg-yellow-400 rounded-full shadow-lg shadow-yellow-400/50"></div>
                            <span className="text-base sm:text-lg text-gray-200">Interactive sessions</span>
                          </div>
                          <div className="flex items-center space-x-3 sm:space-x-4 justify-center lg:justify-start">
                            <div className="w-3 h-3 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50"></div>
                            <span className="text-base sm:text-lg text-gray-200">Personalized consultation</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center space-x-3 sm:space-x-4 justify-center lg:justify-start">
                            <div className="w-3 h-3 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50"></div>
                            <span className="text-base sm:text-lg text-gray-200">Interactive sessions</span>
                          </div>
                          <div className="flex items-center space-x-3 sm:space-x-4 justify-center lg:justify-start">
                            <div className="w-3 h-3 bg-pink-400 rounded-full shadow-lg shadow-pink-400/50"></div>
                            <span className="text-base sm:text-lg text-gray-200">Personalized report with detailed analysis</span>
                          </div>
                        </>
                      )}
                    </motion.div>
                  </div>

                  {/* Image */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                    whileHover={{ scale: 1.05, rotateY: 5 }}
                    className="flex-1 max-w-xs sm:max-w-sm md:max-w-lg mx-auto lg:mx-0"
                    style={{ y: index % 2 === 0 ? y1 : y2 }}
                  >
                    <div className={`relative p-6 sm:p-8 md:p-10 bg-gradient-to-br ${step.color} rounded-3xl shadow-2xl backdrop-blur-xl border border-white/20 overflow-hidden`}>
                      {/* Direct image without white padding wrapper */}
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-40 sm:h-48 md:h-60 object-cover rounded-2xl shadow-2xl"
                      />
                      
                      {/* Floating Cosmic Elements - Mobile Optimized and Reduced */}
                      <motion.div
                        animate={{
                          y: [0, -15, 0],
                          rotate: [0, 8, -8, 0],
                        }}
                        transition={{
                          duration: 6,
                          repeat: Infinity,
                        }}
                        className="absolute -top-4 -right-4 sm:-top-5 sm:-right-5 md:-top-6 md:-right-6 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full opacity-60 sm:opacity-80 shadow-2xl shadow-yellow-400/30 hidden xs:block"
                      />
                      <motion.div
                        animate={{
                          y: [0, 15, 0],
                          x: [0, 10, 0],
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          delay: 1,
                        }}
                        className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 bg-gradient-to-br from-white to-purple-200 rounded-full opacity-50 sm:opacity-70 shadow-xl hidden xs:block"
                      />
                      
                      {/* Cosmic Particles - Hidden on mobile and small screens to prevent overlapping */}
                      <div className="hidden lg:block">
                        {[...Array(3)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-1.5 h-1.5 md:w-2 md:h-2 bg-yellow-300 rounded-full opacity-60"
                            style={{
                              left: `${25 + i * 20}%`,
                              top: `${20 + i * 15}%`,
                            }}
                            animate={{
                              y: [0, -8, 0],
                              opacity: [0.3, 1, 0.3],
                            }}
                            transition={{
                              duration: 2 + i,
                              repeat: Infinity,
                              delay: i * 0.5,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 80 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="mt-16 sm:mt-20 md:mt-32 text-center px-4 sm:px-0"
            >
              <div className="relative p-8 sm:p-12 md:p-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl border border-purple-400/30 shadow-2xl">
                {/* Cosmic Glow Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-pink-600/10 rounded-3xl"
                  animate={{
                    boxShadow: [
                      "0 0 30px rgba(168, 85, 247, 0.3)",
                      "0 0 60px rgba(236, 72, 153, 0.4)",
                      "0 0 30px rgba(168, 85, 247, 0.3)",
                    ],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                />
                
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-white">
                  Ready to Discover Your Cosmic Destiny?
                </h3>
                <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 text-gray-200 max-w-2xl mx-auto leading-relaxed">
                  Our knowledge, analytics, in-depth research and fortune-telling capabilities help us in guiding you with your personal affairs.
                </p>
                <motion.button
                  onClick={() => navigate('/pricing')}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 0 30px rgba(234, 179, 8, 0.5)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 text-black px-8 py-4 sm:px-10 sm:py-4 md:px-12 md:py-5 rounded-full font-bold text-base sm:text-lg md:text-xl shadow-2xl hover:shadow-yellow-400/50 transition-all duration-300 relative overflow-hidden"
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-yellow-200 to-yellow-300"
                    animate={{
                      x: ["-100%", "100%"],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  />
                  <span className="relative z-10">🌟 Book Your Consultation</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </CosmicBackground>
  );
};

export default HowItWorks;
